import type { FetcherArgs } from '@ricoh/ria.client.core/plugins/swr/fetcher';
import type { Func } from '@ricoh/ria.client.core/types/core/function.type';
import type { AxiosError } from 'axios';
import type { Middleware } from 'swr';

import { ClientError, NetworkError, ServiceError } from '@ricoh/ria.client.core/errors';
import { isAxiosError } from 'axios';

const NETWORK_ERROR_CODES = ['ETIMEDOUT', 'ERR_NETWORK'];
const NETWORK_ERROR_STATUSES = [503];

/**
 * Get Axios Error from SWR Error.
 *
 * @param {unknown} error SWR Error.
 *
 * @returns {AxiosError | undefined} Axios Error.
 */
function getAxiosError(error: unknown): AxiosError | undefined {
  if (isAxiosError(error)) {
    return error;
  }

  if (error instanceof ServiceError && isAxiosError(error.cause)) {
    return error.cause;
  }

  return undefined;
}

/**
 * Create Error middleware.
 *
 * @param {Func} showNetworkError Show error.
 *
 * @returns {Middleware} Error middleware.
 */
export function createErrorMiddleware(showNetworkError: Func): Middleware {
  return (useSWRNext) => (key, fetch, options) => {
    if (!fetch) {
      return useSWRNext(key, fetch, options);
    }

    const fetchWithErrorHandler = async (...args: FetcherArgs) => {
      try {
        return await fetch(...args);
      } catch (exception) {
        const axiosError = getAxiosError(exception);
        if (axiosError && (NETWORK_ERROR_CODES.includes(axiosError.code) || NETWORK_ERROR_STATUSES.includes(axiosError.status))) {
          showNetworkError();

          throw new NetworkError({
            status: axiosError.status ?? 503,
            code: axiosError.code,
            message: 'Cannot connect to server',
            cause: axiosError,
          });
        }

        throw ClientError.create({
          module: 'plugin',
          message: 'Execute fetching handler failed',
          cause: exception,
        });
      }
    };

    return useSWRNext(key, fetchWithErrorHandler, options);
  };
}
